package fyi.moko.hotpl.di

import fyi.moko.hotpl.domain.photo.NoPhotoSignals
import fyi.moko.hotpl.domain.photo.PhotoClassifier
import fyi.moko.hotpl.domain.photo.PhotoSignalExtractor
import fyi.moko.hotpl.data.backup.JsBackupManager
import fyi.moko.hotpl.data.backup.LocalBackupManager
import fyi.moko.hotpl.data.image.ImageRotator
import fyi.moko.hotpl.data.image.JsImageRotator
import fyi.moko.hotpl.data.image.JsPlaceImageStore
import fyi.moko.hotpl.data.image.PlaceImageStore
import fyi.moko.hotpl.data.location.CurrentLocationProvider
import fyi.moko.hotpl.data.location.JsCurrentLocationProvider
import fyi.moko.hotpl.data.location.JsLocationContextResolver
import fyi.moko.hotpl.data.location.LocationContextResolver
import fyi.moko.hotpl.data.repository.JsPlaceRepositoryImpl
import fyi.moko.hotpl.data.repository.JsYellowPageRepository
import fyi.moko.hotpl.data.service.PlacesSearchConfig
import fyi.moko.hotpl.domain.repository.PlaceRepository
import fyi.moko.hotpl.domain.repository.YellowPageRepository
import fyi.moko.hotpl.domain.service.AuthService
import org.koin.core.context.startKoin
import fyi.moko.hotpl.domain.service.PhotoSyncQueue
import org.koin.core.qualifier.named
import org.koin.dsl.module
import org.koin.mp.KoinPlatform

val jsPlatformModule = module {
    single<LocationContextResolver> { JsLocationContextResolver() }
    single<CurrentLocationProvider> { JsCurrentLocationProvider() }
    single<PlaceImageStore>(named(LOCAL_IMAGE_STORE)) {
        JsPlaceImageStore(userIdProvider = { get<AuthService>().getCurrentUserId() })
    }
    single<ImageRotator> { JsImageRotator() }
    single<PhotoSyncQueue> { get<JsPlaceRepositoryImpl>() }
    // 웹도 아직 신호 추출기가 없다 — 브라우저에서 돌릴 온디바이스 분류기를 붙이지 않았다.
    // 모든 사진이 미분류로 들어가고 사용자가 화면에서 정한다.
    single<PhotoSignalExtractor> { NoPhotoSignals }
    single { PhotoClassifier(get()) }
    single { JsPlaceRepositoryImpl(authService = get()) }
    single<PlaceRepository> { get<JsPlaceRepositoryImpl>() }
    single<YellowPageRepository> { JsYellowPageRepository() }
    single<LocalBackupManager> { JsBackupManager(get(), get()) }
    single<fyi.moko.hotpl.domain.service.ConnectivityObserver> { fyi.moko.hotpl.service.JsConnectivityObserver() }
    single<fyi.moko.hotpl.domain.service.ChangelogStore> {
        fyi.moko.hotpl.data.service.JsChangelogStore()
    }
    single<fyi.moko.hotpl.domain.service.HomeViewPreference> {
        fyi.moko.hotpl.data.service.JsHomeViewPreference()
    }
    single<fyi.moko.hotpl.domain.service.EmotionLabelPreference> {
        fyi.moko.hotpl.data.service.JsEmotionLabelPreference()
    }
    single<fyi.moko.hotpl.domain.service.UsageTrackingPreference> {
        fyi.moko.hotpl.data.service.JsUsageTrackingPreference()
    }
    single<fyi.moko.hotpl.domain.service.DestinationHistoryStore> {
        fyi.moko.hotpl.data.service.JsDestinationHistoryStore()
    }
}

/**
 * Entry point for the JS/Compose-web host to start Koin with the shared graph
 * plus the JS platform module. Called from `main()` before composing the UI.
 * Idempotent: mirrors `iosMain`'s `initKoin()` — a repeated call is a no-op once
 * the global Koin context is already initialized (HMR / re-entry safe).
 *
 * NOTE: `sharedModule` wires the GitLive Firebase `AuthService` /
 * `NetworkPlaceDataSource`. On JS these reach a default Firebase app that is
 * not yet initialized (no `google-services.json` equivalent on web), so sync
 * will no-op until a web Firebase config is injected — see `Main.kt` and
 * `docs/handoff_phase2_status.md`. Exceptions thrown there are caught inside
 * `SyncCoordinator`'s coroutines and do not block the local-first UI.
 */
fun initKoin(placesApiKey: String? = null) {
    if (KoinPlatform.getKoinOrNull() == null) {
        startKoin {
            modules(
                sharedModule,
                jsPlatformModule,
                module { single { PlacesSearchConfig(apiKey = placesApiKey) } },
            )
        }
    }
}
